import React from 'react';
import { reportError } from '../utils/errorHandling';

function Premium3DHardwareSelector({ selectedHardware, onHardwareSelect, onQuantityChange }) {
    try {
        const hardwareTypes = [
            {
                icon: "fa-laptop",
                title: "Laptops",
                description: "Transform old laptops into learning opportunities",
                gradient: "from-blue-500/20 to-purple-500/20",
                glowColor: "blue",
                co2Impact: 80
            },
            {
                icon: "fa-desktop",
                title: "Desktop PCs",
                description: "Turn desktop computers into educational tools",
                gradient: "from-green-500/20 to-teal-500/20",
                glowColor: "green",
                co2Impact: 120
            },
            {
                icon: "fa-server",
                title: "Servers",
                description: "Convert enterprise servers to learning infrastructure",
                gradient: "from-purple-500/20 to-pink-500/20",
                glowColor: "purple",
                co2Impact: 200
            },
            {
                icon: "fa-microchip",
                title: "Components",
                description: "Individual parts that build complete systems",
                gradient: "from-orange-500/20 to-red-500/20",
                glowColor: "orange",
                co2Impact: 40
            },
            {
                icon: "fa-tablet-alt",
                title: "Tablets",
                description: "Mobile devices for accessible learning",
                gradient: "from-cyan-500/20 to-blue-500/20",
                glowColor: "cyan",
                co2Impact: 50
            },
            {
                icon: "fa-mobile-alt",
                title: "Smartphones",
                description: "Connect communities through mobile technology",
                gradient: "from-pink-500/20 to-rose-500/20",
                glowColor: "pink",
                co2Impact: 30
            }
        ];

        const getGlowClasses = (color, isSelected) => {
            const intensity = isSelected ? 'medium' : 'light';
            const glowMap = {
                blue: intensity === 'medium' ? 'shadow-blue-500/50' : 'shadow-blue-500/20',
                green: intensity === 'medium' ? 'shadow-green-500/50' : 'shadow-green-500/20',
                purple: intensity === 'medium' ? 'shadow-purple-500/50' : 'shadow-purple-500/20',
                orange: intensity === 'medium' ? 'shadow-orange-500/50' : 'shadow-orange-500/20',
                cyan: intensity === 'medium' ? 'shadow-cyan-500/50' : 'shadow-cyan-500/20',
                pink: intensity === 'medium' ? 'shadow-pink-500/50' : 'shadow-pink-500/20'
            };
            return glowMap[color] || 'shadow-blue-500/20';
        };

        return (
            <div className="premium-hardware-grid">
                <style jsx>{`
                    .premium-hardware-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                        gap: 1.5rem;
                        perspective: 1000px;
                        padding: 1rem;
                    }
                      .hardware-3d-card {
                        transform-style: preserve-3d;
                        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
                        cursor: pointer;
                        position: relative;
                        overflow: hidden;
                        will-change: transform;
                        transform: translateZ(0);
                        backface-visibility: hidden;
                        -webkit-font-smoothing: antialiased;
                    }
                    
                    /* Add magnetic hover effect */
                    @media (hover: hover) {
                        .hardware-3d-card {
                            transition: transform 0.2s ease-out;
                        }
                    }
                    
                    /* Premium glass refraction effect */
                    .hardware-3d-card::before {
                        content: '';
                        position: absolute;
                        top: -50%;
                        left: -50%;
                        width: 200%;
                        height: 200%;
                        background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
                        opacity: 0;
                        transition: opacity 0.3s ease;
                        pointer-events: none;
                        z-index: 1;
                    }
                    
                    .hardware-3d-card:hover::before {
                        opacity: 1;
                    }
                    
                    .hardware-3d-card::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, transparent 100%);
                        border-radius: inherit;
                        z-index: 1;
                        pointer-events: none;
                    }
                    
                    .hardware-3d-card::after {
                        content: '';
                        position: absolute;
                        top: 1px;
                        left: 1px;
                        right: 1px;
                        bottom: 1px;
                        background: rgba(0,0,0,0.4);
                        backdrop-filter: blur(20px);
                        border-radius: inherit;
                        z-index: -1;
                    }
                      .hardware-3d-card:hover {
                        transform: translateY(-6px) rotateX(2deg) rotateY(1deg) scale(1.02);
                    }
                    
                    .hardware-3d-card.selected {
                        transform: translateY(-4px) rotateX(1deg) scale(1.03);
                    }
                    
                    .hardware-3d-card.selected:hover {
                        transform: translateY(-8px) rotateX(3deg) rotateY(2deg) scale(1.05);
                    }
                    
                    .premium-icon-container {
                        position: relative;
                        background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%);
                        backdrop-filter: blur(10px);
                        border: 1px solid rgba(255,255,255,0.2);
                        transition: all 0.4s ease;
                    }
                    
                    .premium-icon-container::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%);
                        transform: translateX(-100%);
                        transition: transform 0.6s ease;
                    }
                    
                    .hardware-3d-card:hover .premium-icon-container::before {
                        transform: translateX(100%);
                    }
                    
                    .premium-content {
                        position: relative;
                        z-index: 2;
                    }
                    
                    .impact-badge {
                        background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.3) 100%);
                        backdrop-filter: blur(10px);
                        border: 1px solid rgba(16, 185, 129, 0.3);
                        position: relative;
                        overflow: hidden;
                    }
                    
                    .impact-badge::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.2), transparent);
                        transition: left 0.5s ease;
                    }
                    
                    .hardware-3d-card:hover .impact-badge::before {
                        left: 100%;
                    }
                    
                    .quantity-controller {
                        background: rgba(0,0,0,0.6);
                        backdrop-filter: blur(20px);
                        border: 1px solid rgba(255,255,255,0.1);
                    }
                    
                    .quantity-btn {
                        background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
                        border: 1px solid rgba(255,255,255,0.2);
                        transition: all 0.3s ease;
                    }
                    
                    .quantity-btn:hover {
                        background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%);
                        transform: scale(1.1);
                    }
                    
                    .selection-indicator {
                        position: absolute;
                        top: 1rem;
                        right: 1rem;
                        background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                        border: 2px solid rgba(255,255,255,0.3);
                        animation: pulseGlow 2s infinite;
                        z-index: 3;
                    }
                    
                    @keyframes pulseGlow {
                        0%, 100% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.6); }
                        50% { box-shadow: 0 0 30px rgba(16, 185, 129, 0.8), 0 0 40px rgba(16, 185, 129, 0.4); }
                    }
                      @media (max-width: 768px) {
                        .premium-hardware-grid {
                            grid-template-columns: 1fr;
                            gap: 1rem;
                        }
                        
                        .hardware-3d-card:hover {
                            transform: translateY(-8px) scale(1.02);
                        }
                    }
                    
                    /* Optimize animations for performance */
                    @media (prefers-reduced-motion: reduce) {
                        .hardware-3d-card,
                        .hardware-3d-card *,
                        .selection-indicator {
                            animation: none !important;
                            transition: none !important;
                        }
                    }
                    
                    /* GPU acceleration for smooth animations */
                    .hardware-3d-card {
                        transform: translateZ(0);
                        backface-visibility: hidden;
                        -webkit-font-smoothing: antialiased;
                    }
                    
                    /* Magnetic effect for premium feel */
                    .hardware-3d-card:hover {
                        filter: brightness(1.1) saturate(1.2);
                    }
                    
                    .hardware-3d-card.selected {
                        filter: brightness(1.15) saturate(1.3);
                    }
                `}</style>

                {hardwareTypes.map((hardware, index) => {
                    const isSelected = selectedHardware.some(h => h.type === hardware.title);
                    const selectedItem = selectedHardware.find(h => h.type === hardware.title);
                    
                    return (
                        <div
                            key={index}
                            onClick={() => onHardwareSelect(hardware)}
                            className={`hardware-3d-card ${isSelected ? 'selected' : ''} 
                                       relative rounded-2xl p-6 cursor-pointer 
                                       bg-gradient-to-br ${hardware.gradient}
                                       border border-white/20 backdrop-blur-xl
                                       shadow-2xl ${getGlowClasses(hardware.glowColor, isSelected)}`}
                        >
                            {/* Selection Indicator */}
                            {isSelected && (
                                <div className="selection-indicator w-8 h-8 rounded-full flex items-center justify-center">
                                    <i className="fas fa-check text-white text-sm"></i>
                                </div>
                            )}

                            <div className="premium-content">
                                {/* Premium Icon Container */}
                                <div className="premium-icon-container w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                                    <i className={`fas ${hardware.icon} text-2xl text-white`}></i>
                                </div>

                                {/* Content */}
                                <div className="text-center">
                                    <h3 className="text-xl font-bold text-white mb-2">{hardware.title}</h3>
                                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">{hardware.description}</p>
                                    
                                    {/* Impact Badge */}
                                    <div className="impact-badge rounded-lg px-3 py-2 mb-4">
                                        <div className="flex items-center justify-center text-sm text-green-300">
                                            <i className="fas fa-leaf mr-2"></i>
                                            <span className="font-medium">{hardware.co2Impact}kg CO₂ saved</span>
                                        </div>
                                    </div>

                                    {/* Quantity Controller */}
                                    {isSelected && (
                                        <div className="quantity-controller rounded-xl p-3 mt-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-300 text-sm font-medium">Quantity</span>
                                                <div className="flex items-center space-x-3">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const current = selectedItem?.quantity || 1;
                                                            if (current > 1) {
                                                                onQuantityChange(hardware.title, current - 1);
                                                            }
                                                        }}
                                                        className="quantity-btn w-8 h-8 rounded-lg flex items-center justify-center text-white"
                                                    >
                                                        <i className="fas fa-minus text-xs"></i>
                                                    </button>
                                                    <span className="text-white font-bold text-lg min-w-[2rem] text-center">
                                                        {selectedItem?.quantity || 1}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const current = selectedItem?.quantity || 1;
                                                            onQuantityChange(hardware.title, current + 1);
                                                        }}
                                                        className="quantity-btn w-8 h-8 rounded-lg flex items-center justify-center text-white"
                                                    >
                                                        <i className="fas fa-plus text-xs"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    } catch (error) {
        console.error('Premium3DHardwareSelector error:', error);
        reportError(error);
        return null;
    }
}

export default Premium3DHardwareSelector;
